import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, X } from "lucide-react";

const API_EXT = process.env.REACT_APP_API_URL;

/**
 * TrialBanner — non-intrusive countdown banner shown on DashboardPage when the
 * user is in trial. Becomes more urgent as the deadline approaches.
 * Honors per-day dismissal via localStorage.
 */
export const TrialBanner = () => {
  const [user, setUser] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("nautiqo_token");
    if (!token) return;
    axios.get(`${API_EXT}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUser(res.data))
      .catch(() => {});
    // dismissed per day to avoid being annoying
    const today = new Date().toISOString().slice(0, 10);
    const last = localStorage.getItem("nautiqo_trial_banner_dismissed");
    if (last === today) setDismissed(true);
  }, []);

  if (!user || dismissed) return null;

  const status = (user.subscription_status || "").toLowerCase();
  const isTrial = status === "trial" || status === "trialing";
  if (!isTrial || !user.subscription_end_date) return null;

  const end = new Date(user.subscription_end_date);
  const daysLeft = Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
  if (daysLeft < 0) return null;

  // Tier mapping: gentle → urgent → critical
  const tier =
    daysLeft <= 1 ? "critical"
    : daysLeft <= 3 ? "urgent"
    : daysLeft <= 7 ? "warm"
    : "gentle";

  const styles = {
    gentle:   { bg: "bg-blue-500/10",   border: "border-blue-400/20",   text: "text-blue-100",   icon: "text-blue-300",   chip: "bg-blue-500/20 text-blue-200" },
    warm:     { bg: "bg-amber-500/10",  border: "border-amber-400/25",  text: "text-amber-100",  icon: "text-amber-300",  chip: "bg-amber-500/20 text-amber-200" },
    urgent:   { bg: "bg-orange-500/10", border: "border-orange-400/30", text: "text-orange-100", icon: "text-orange-300", chip: "bg-orange-500/25 text-orange-100" },
    critical: { bg: "bg-red-500/10",    border: "border-red-400/35",    text: "text-red-100",    icon: "text-red-300",    chip: "bg-red-500/25 text-red-100" },
  }[tier];

  const headline =
    tier === "critical" ? "Il trial scade oggi"
    : daysLeft === 1   ? "Il trial scade domani"
    : `Il trial scade tra ${daysLeft} giorni`;

  const subtext =
    tier === "critical"
      ? "Attiva il piano ora per non perdere l'accesso al servizio."
      : tier === "urgent"
        ? "Attiva il piano per non perdere prenotazioni e dati operativi."
        : tier === "warm"
          ? "Attiva il piano e ricevi automaticamente le prossime fatture."
          : "Hai ancora tempo per attivare il piano. Cancella quando vuoi.";

  const handleDismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem("nautiqo_trial_banner_dismissed", today);
    setDismissed(true);
  };

  return (
    <div
      className={`flex items-center gap-3 sm:gap-4 ${styles.bg} ${styles.border} border rounded-2xl px-4 py-3 sm:px-5 sm:py-4 mb-5 backdrop-blur-sm`}
      data-testid="trial-banner"
      data-tier={tier}
    >
      <Clock className={`w-5 h-5 flex-shrink-0 ${styles.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${styles.text}`}>
          {headline}
          <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${styles.chip}`}>
            {daysLeft}g
          </span>
        </p>
        <p className="text-xs text-slate-400 mt-0.5 hidden sm:block">{subtext}</p>
      </div>
      <Link
        to="/pricing"
        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition shadow-sm ${
          tier === "critical" || tier === "urgent"
            ? "bg-white text-slate-900 hover:bg-slate-100"
            : "bg-white/15 hover:bg-white/25 text-white border border-white/15"
        }`}
        data-testid="trial-banner-cta"
      >
        Attiva piano
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      {tier !== "critical" && tier !== "urgent" && (
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-slate-300 transition flex-shrink-0"
          aria-label="Nascondi avviso fino a domani"
          data-testid="trial-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
